import { Component, OnDestroy, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MediatiqueService } from '../mediatique.service';
import { SwalUtils } from '../../../../shared/utils/swal.utils';

import { isAllowedImage, readImagePreview, IMAGE_FORMAT_ERROR } from '../../../../shared/utils/image-upload';
export interface MediaItem {
  type: 'image' | 'video';
  file?: File;
  preview?: string;
  videoLink?: string;
}

@Component({
  selector: 'app-mediatique-form',
  standalone: false,
  templateUrl: './mediatique-form.component.html',
  styleUrls: ['./mediatique-form.component.scss'],
})
export class MediatiqueFormComponent implements OnDestroy {
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  title = signal('');
  eventDate = signal('');
  category = signal('');

  // Unified media list
  mediaItems = signal<MediaItem[]>([]);

  // Modal state
  showModal = signal(false);
  modalTab = signal<'image' | 'video'>('image');
  modalVideoLink = signal('');
  modalFiles = signal<{ file: File; preview: string }[]>([]);
  modalError = signal<string | null>(null);

  private destroy$ = new Subject<void>();

  constructor(
    private mediatiqueService: MediatiqueService,
    private router: Router
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Modal ──────────────────────────────────────────────────────────────────

  openModal(): void {
    this.modalTab.set('image');
    this.modalVideoLink.set('');
    this.modalFiles.set([]);
    this.modalError.set(null);
    this.showModal.set(true);
  }

  closeModal(): void {
    this.showModal.set(false);
  }

  onModalFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = '';
    if (!files.length) return;

    const errors: string[] = [];
    for (const file of files) {
      if (!isAllowedImage(file)) { errors.push(`${file.name} : ${IMAGE_FORMAT_ERROR}`); continue; }
      if (file.size > 10 * 1024 * 1024) { errors.push(`${file.name} : fichier trop volumineux (max 10 Mo)`); continue; }
      const entry = { file, preview: '' };
      this.modalFiles.set([...this.modalFiles(), entry]);
      readImagePreview(file, (url) => {
        entry.preview = url;
        this.modalFiles.set([...this.modalFiles()]);
      });
    }
    this.modalError.set(errors.length ? errors.join(' · ') : null);
  }

  removeModalFile(index: number): void {
    const files = [...this.modalFiles()];
    files.splice(index, 1);
    this.modalFiles.set(files);
  }

  confirmAdd(): void {
    if (this.modalTab() === 'image') {
      const files = this.modalFiles();
      if (!files.length) { this.modalError.set('Sélectionnez au moins une image'); return; }
      this.mediaItems.set([
        ...this.mediaItems(),
        ...files.map(f => ({ type: 'image' as const, file: f.file, preview: f.preview })),
      ]);
    } else {
      const link = this.modalVideoLink().trim();
      if (!link) { this.modalError.set('Entrez un lien vidéo'); return; }
      this.mediaItems.set([...this.mediaItems(), { type: 'video', videoLink: link }]);
    }
    this.closeModal();
  }

  removeMediaItem(index: number): void {
    const items = [...this.mediaItems()];
    items.splice(index, 1);
    this.mediaItems.set(items);
  }

  videoThumbnail(link: string): string | null {
    const yt = link.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    return yt ? `https://img.youtube.com/vi/${yt[1]}/mqdefault.jpg` : null;
  }

  // ── Save ──────────────────────────────────────────────────────────────────

  async onSave(): Promise<void> {
    if (!this.title().trim()) { this.errorMessage.set('Le titre est requis'); return; }
    if (!this.eventDate()) { this.errorMessage.set('La date est requise'); return; }

    const confirmed = await SwalUtils.confirmGreen(
      'Créer l\'album',
      'Voulez-vous créer cet album médiatique ?',
      'Créer'
    );
    if (!confirmed.isConfirmed) return;

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const form = new FormData();
    form.append('title', this.title().trim());
    form.append('event_date', this.eventDate());
    if (this.category()) form.append('media_tags', this.category());

    this.mediaItems().forEach(item => {
      if (item.type === 'image' && item.file) {
        form.append('photos', item.file);
      } else if (item.type === 'video' && item.videoLink) {
        form.append('video_links', item.videoLink);
      }
    });

    this.mediatiqueService.createAlbum(form).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        SwalUtils.success('Album créé', 'L\'album a été créé avec succès.');
        if (res?.data?.id_media_library) {
          this.router.navigate(['/federation/mediatique', res.data.id_media_library]);
        } else {
          this.router.navigate(['/federation/mediatique']);
        }
      },
      error: (err) => {
        this.isLoading.set(false);
        SwalUtils.error('Erreur', err?.error?.message || 'Une erreur est survenue lors de la création de l\'album.');
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/federation/mediatique']);
  }
}
